import { parsePhoneNumber, CountryCode } from 'libphonenumber-js';
// Types
import { GetSupportedOtpCountries } from 'api/otp/types/GetSupportedOtpCountries';

/**
 * Phone number formatting utilities using libphonenumber-js
 */

/**
 * Detects the likely country code based on phone number format
 * @param phoneNumber The full phone number to detect country from
 * @returns The detected country code or undefined if couldn't detect
 */
export const detectCountryFromPhone = (
  phoneNumber: string
): CountryCode | undefined => {
  try {
    const parsedNumber = parsePhoneNumber(phoneNumber);
    return parsedNumber?.country;
  } catch {
    return undefined;
  }
};

/**
 * Formats a phone number according to the specified country format
 * @param phoneNumber The phone number to format
 * @param countryCode Optional country code to use for formatting (if not provided, will try to detect)
 * @returns The formatted phone number
 */
export const formatPhoneNumber = (
  phoneNumber: string,
  countryCode?: CountryCode
): string => {
  try {
    const parsedNumber = parsePhoneNumber(phoneNumber, countryCode);
    return parsedNumber.formatInternational();
  } catch {
    // If parsing fails, return the original number
    return phoneNumber;
  }
};

export const maskPhone = (phone: string): string => {
  if (!phone) return '';

  try {
    const parsedNumber = parsePhoneNumber(phone);
    const formattedPhone = parsedNumber.formatInternational();

    // Split the number into international and national parts
    const internationalPart = formattedPhone.split(' ')[0]; // e.g. "+1" or "+44"
    const nationalPart = formattedPhone
      .substring(internationalPart.length)
      .trim();

    // Get all digits from the national part
    const digits = nationalPart.replace(/\D/g, '');

    if (digits.length <= 6) {
      return formattedPhone;
    }

    // Create a masked version of the national part keeping first 3 and last 3 digits visible
    let digitCounter = 0;
    const maskedNational = nationalPart.replace(/\d/g, (match) => {
      digitCounter++;
      if (digitCounter <= 3 || digitCounter > digits.length - 3) {
        return match;
      }
      return '*';
    });

    return `${internationalPart} ${maskedNational}`;
  } catch {
    // If parsing fails, use basic masking
    const digits = phone.replace(/\D/g, '');

    if (digits.length <= 6) {
      return phone;
    }

    let digitCounter = 0;
    return phone.replace(/\d/g, (match) => {
      digitCounter++;
      if (digitCounter <= 3 || digitCounter > digits.length - 3) {
        return match;
      }
      return '*';
    });
  }
};

export const checkIsCountrySupported = (
  supportedCountries?: GetSupportedOtpCountries['getSupportedOtpCountries'],
  countryCode?: CountryCode
): boolean => {
  return (supportedCountries ?? []).some((country) => country === countryCode);
};
